<template>
  <b-modal
    size="xl"
    ok-title="Add"
    title="Patient Scans"
    title-tag="h5"
    cancel-title="Cancel"
    cancel-variant="outline-primary"
    id="idFromCloudModal"
    :ok-disabled="!selectedItem"
    @ok="onAttachPatientScan"
    @show="getPatientScans"
    @hidden="resetSelection"
  >
    <b-row>
      <b-col>
        <label for="selectStatus">Status</label>
        <b-form-select
          :disabled="showSpinner"
          size="sm"
          id="selectStatus"
          v-model="selectedStatus"
          :options="statusOptions"
        ></b-form-select>
      </b-col>
      <b-col>
        <label for="selectedUploadedWithin">Uploaded within</label>
        <b-form-select
          :disabled="showSpinner"
          size="sm"
          id="selectedUploadedWithin"
          v-model="selectedUploadedWithin"
          :options="uploadedWithinOptions"
        ></b-form-select>
      </b-col>
      <b-col>
        <label for="selectedUploadedBy">Uploaded by</label>
        <b-form-select
          :disabled="showSpinner"
          size="sm"
          id="selectedUploadedBy"
          v-model="selectedUploadedBy"
          :options="uploadedByOptions"
        ></b-form-select>
      </b-col>
      <b-col>
        <label for="filterInput">Search</label>
        <b-form-input
          :disabled="showSpinner"
          size="sm"
          v-model="filter"
          type="search"
          id="filterInput"
          placeholder="Type to Search"
        ></b-form-input>
      </b-col>
    </b-row>
    <br />
    <b-row>
      <b-table
        v-if="!showSpinner"
        id="idPatientScansTable"
        style="font-size: small; min-height: 8rem"
        head-variant="light"
        outlined
        responsive
        hover
        selectable
        select-mode="single"
        small
        per-page="10"
        :items="scans"
        :fields="fields"
        :current-page="currentPage"
        :filter="filter"
        :filter-ignored-fields="ignoreFields"
        @filtered="onFiltered"
        @row-clicked="onRowSelected"
      >
        <template v-slot:head(actions)>
          <div id="actions-header-div">
            <span v-bind:style="specialHeader">Actions</span>
            <b-dropdown
              id="more-columns-dropdown"
              text="More Columns"
              size="sm"
              variant="outline-primary"
              dropleft
              ref="moreColumnsDropdown"
            >
              <b-dropdown-form>
                <b-form-checkbox
                  class="column-dropdown"
                  value="SeriesDescription"
                  v-model="checkedColumns"
                  ><b>Series Description</b></b-form-checkbox
                >
                <b-form-checkbox
                  class="column-dropdown"
                  value="StudyDescription"
                  v-model="checkedColumns"
                  ><b>Study Description</b></b-form-checkbox
                >
                <b-button
                  size="sm"
                  variant="outline-primary"
                  id="apply-columns-btn"
                  @click="applyMoreColumns()"
                  >Apply</b-button
                >
              </b-dropdown-form>
            </b-dropdown>
          </div>
        </template>
        <template v-slot:cell(actions)="row">
          <!-- Dicom Viewer Button - all buttons with all styles initialized, but only one visible at a time -->
          <b-button
            class="viewer-btn"
            v-if="row.item.ProvisioningStatus === 'Unprovisioned'"
            size="sm"
            @click="handleDicomButtonClick(row.item)"
            variant="outline-primary"
          >
            <b-icon scale=".9" icon="eye"></b-icon> Click to Load
          </b-button>

          <!-- Completed Button -->
          <b-button
            class="viewer-btn"
            v-else-if="row.item.ProvisioningStatus === 'Completed'"
            size="sm"
            @click="handleDicomButtonClick(row.item)"
            variant="outline-success"
          >
            <b-icon scale=".9" icon="check-circle-fill"></b-icon> Click to View
          </b-button>

          <!-- Failed Button -->
          <b-button
            class="viewer-btn"
            v-else-if="row.item.ProvisioningStatus === 'Failed'"
            size="sm"
            @click="handleDicomButtonClick(row.item)"
            variant="outline-danger"
          >
            <b-icon scale=".9" icon="x-circle-fill"></b-icon> Click to Retry
          </b-button>

          <!-- Purging Button -->
          <b-button
            class="viewer-btn"
            v-else-if="row.item.ProvisioningStatus === 'Purging'"
            disabled
            size="sm"
            @click="handleDicomButtonClick(row.item)"
            variant="outline-primary"
          >
            <b-icon scale=".9" icon="trash-fill" animation="throb"></b-icon>
            Purging...</b-button
          >
          <!-- For provisioning statuses InProgress or status not set yet, show the loading button -->
          <b-button
            class="viewer-btn"
            v-else
            size="sm"
            disabled
            variant="outline-primary"
          >
            <b-icon scale=".9" icon="arrow-clockwise" animation="spin"></b-icon>
            Loading...
          </b-button>

          <b-button
            size="sm"
            variant="outline-primary"
            class="download-btn"
            @click="downloadPatientScanClicked(row.item)"
            ><b-icon scale=".9" icon="download"></b-icon> Download</b-button
          >
        </template>
      </b-table>
    </b-row>
    <b-pagination
      style="margin-right: 1rem"
      v-if="!showSpinner"
      v-model="currentPage"
      :total-rows="totalRows"
      first-number
      last-number
      per-page="10"
      align="right"
    />
    <div style="text-align: center">
      <b-spinner
        variant="warning"
        class="m-5"
        style="width: 3rem; height: 3rem"
        v-if="showSpinner"
      />
    </div>
  </b-modal>
</template>

<script>
import moment from "moment";
import _ from "lodash";

export default {
  data() {
    return {
      totalTimeVisible: 1,
      tabFocus: true,
      totalRows: 1,
      currentPage: 1,
      scans: [],
      selectedItem: null,
      selectedStatus: "Available",
      selectedUploadedWithin: "7",
      statusOptions: [
        { value: "Available", text: "Available" },
        { value: "Assigned", text: "Assigned" },
      ],
      selectedUploadedBy: "All",
      uploadedByOptions: [{ value: "All", text: "All" }],
      uploadedWithinOptions: [
        { value: "7", text: "Last 7 days" },
        { value: "30", text: "Last 30 days" },
        { value: "-1", text: "All" },
      ],
      filter: null,
      showSpinner: true,
      fields: [
        { key: "PatientName", sortable: true },
        { key: "DateOfBirth", sortable: true },
        { key: "Uploader", sortable: true },
        { key: "NumberOfSlices", sortable: true },
        { key: "ModificationDate", sortable: true },
        { key: "ReferringPhysician", sortable: true },
        { key: "Actions", sortable: false },
      ],
      ignoreFields: ["id", "fileName"],
      statusChecksModalTimer: null,
      specialHeader: {
        position: "absolute",
        top: 10,
        "margin-top": "0.95rem",
      },
      checkedColumns: [],
      appliedCheckedColumns: [],
      dicomTagMap: {
        SeriesDescription: "(0008,103E)",
        StudyDescription: "(0008,1030)",
      },
    };
  },
  methods: {
    resetSelection() {
      this.selectedUploadedBy = "All";
      this.filter = null;
      this.selectedItem = null;
      this.selectedStatus = "Available";
      // Stop the update timer before we close the modal
      clearInterval(this.statusChecksModalTimer);
    },
    async getPatientScans() {
      this.showSpinner = true;
      // Assigned = true, Available = null
      const selectedStatus = this.selectedStatus === "Assigned" ? true : null;
      this.opts = {
        additionalDataFilter: {
          "jointReplacement.tprhs.assigned": selectedStatus,
        },
      };
      const data = await this.$patientScansMetadataApi.patientscansMetadataGet(
        this.opts
      );
      var scans = await data.content.map(async (scan) => {
        var uploadedBy = "";
        if (!scan.metadata.isManualUpload) {
          try {
            const facility = await this.$buhApi.branchesScanfacilitiesIdGet(
              scan.metadata.uploadedByScanFacility
            );
            uploadedBy = facility.facilityName;
          } catch (e) {
            uploadedBy = "Stryker";
          }
        } else {
          uploadedBy = scan.metadata.uploadedByUser;
        }
        return {
          id: scan.metadata.patientScanId,
          fileName: scan.metadata.patientScanDescriptor,
          PatientName: scan.patient.patientName,
          DateOfBirth: scan.patient.patientDateOfBirth
            ? moment(
                new Date(
                  scan.patient.patientDateOfBirth.getUTCFullYear(),
                  scan.patient.patientDateOfBirth.getUTCMonth(),
                  scan.patient.patientDateOfBirth.getUTCDate()
                )
              ).format("l")
            : "",
          Uploader: uploadedBy,
          NumberOfSlices: scan.metadata.totalNumberOfImages,
          ModificationDate: moment(
            scan.metadata.dateTimeLastModified + "Z"
          ).format("l"),
          ReferringPhysician: scan.metadata.referringPhysicianName,
          ProvisioningStatus: "InProgress",
          SeriesDescription: "",
          StudyDescription: "",
          DateTimeUploaded: scan.metadata.dateTimeUploaded,
        };
      });
      await Promise.all(scans).then((scans) => {
        if (this.selectedUploadedBy !== "All") {
          scans = scans.filter(
            (scan) => scan.Uploader === this.selectedUploadedBy
          );
        }
        // Filter by "Uploaded Within"
        if (this.selectedUploadedWithin > 0) {
          const today = new Date();
          var startOfRange = new Date();
          startOfRange.setDate(today.getDate() - this.selectedUploadedWithin);
          scans = scans.filter((scan) => startOfRange <= scan.DateTimeUploaded);
        }
        this.scans = scans;
        this.totalRows = scans.length;
        if (this.selectedUploadedBy === "All") {
          this.uploadedByOptions = [{ value: "All", text: "All" }];
          scans
            .map((scan) => scan.Uploader)
            .filter((value, index, self) => self.indexOf(value) === index)
            .map((uploader) => {
              this.uploadedByOptions.push({ value: uploader, text: uploader });
            });
        }
      });

      // Check all statuses intially after getting all the scans
      await this.getStatusesForScans(true);

      // And get the dicom tag data if it's checked
      await this.getAdditionalDicomTagData();

      // After done getting all the scans, start the interval to check the statuses periodically (first stop old timers)
      clearInterval(this.statusChecksModalTimer);
      this.startBackendStatusChecks();
    },
    onAttachPatientScan() {
      // Stop the update timer before we close the modal
      clearInterval(this.statusChecksModalTimer);
      this.$emit("added", this.selectedItem);
    },
    onRowSelected(item) {
      this.selectedItem = item;
    },
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    applyMoreColumns() {
      // Only do anything if the columns changed
      if (
        !_.isEqual(
          this.appliedCheckedColumns.sort(),
          this.checkedColumns.sort()
        )
      ) {
        // First hide the open dialog
        this.$refs.moreColumnsDropdown.hide(true);

        // Initialize with the fields that are always there
        var newFields = [
          { key: "PatientName", sortable: true },
          { key: "DateOfBirth", sortable: true },
          { key: "Uploader", sortable: true },
          { key: "NumberOfSlices", sortable: true },
          { key: "ModificationDate", sortable: true },
          { key: "ReferringPhysician", sortable: true },
        ];

        // Then add the checked columns (or don't add unchecked columns)
        for (const checkedColumn of this.checkedColumns) {
          newFields.push({ key: checkedColumn, sortable: true });
        }

        // Actions field always goes last
        newFields.push({ key: "Actions", sortable: false });
        this.fields = newFields;

        // Populate the new column(s) with data only if we're adding or changing a column
        // (removing a column is already done so no need to call this method)
        if (
          this.appliedCheckedColumns.length < this.checkedColumns.length ||
          (!_.isEqual(
            this.appliedCheckedColumns.sort(),
            this.checkedColumns.sort()
          ) &&
            this.appliedCheckedColumns.length == this.checkedColumns.length)
        ) {
          this.getAdditionalDicomTagData();
        }

        // Then update the object
        this.appliedCheckedColumns = this.checkedColumns;
      }
    },
    async getAdditionalDicomTagData() {
      // Gets the additional specified dicom data for each scan
      var dicomTagsToQuery = [];
      this.checkedColumns.forEach((element) => {
        dicomTagsToQuery.push(this.dicomTagMap[element]);
      });

      this.showSpinner = true;
      // Get the data for each scan
      for (var scan of this.scans) {
        const dicomData =
          await this.$patientScansMetadataApi.patientscansIdMetadataGet(
            scan.id,
            { dicomTags: dicomTagsToQuery }
          );
        this.checkedColumns.forEach((element) => {
          scan[element] = dicomData.dicomTags[this.dicomTagMap[element]];
        });
      }
      this.showSpinner = false;
    },
    async getStatusesForScans(updateAllScans = false) {
      // Don't make backend calls if there aren't any patient scans to check
      if (this.scans.length == 0) {
        return;
      }
      // Create an array with all scans that should be updated
      let patientScanIds = this.scans
        .filter(
          (scan) => scan.ProvisioningStatus === "InProgress" || updateAllScans
        )
        .map((scan) => scan.id);

      // If no scans to update, just return
      if (!patientScanIds.length) return;

      // Get Statuses for all Scans in the array
      const metadataResponse =
        await this.$patientScanViewerProvisioningApi.patientscansViewermetadataGet(
          { patientScanIds: patientScanIds, pageSize: 0 }
        );

      // Update Statuses and show success alert if needed
      metadataResponse.content.map((response) => {
        const scan =
          this.scans[
            this.scans.findIndex((el) => el.id === response.patientScanId)
          ];
        // If this update resulted in a completed scan and we're not still initializing, show a success alert
        if (
          response.provisioningStatus === "Completed" &&
          scan.ProvisioningStatus === "InProgress" &&
          this.statusChecksTimer !== null
        ) {
          this.$root.$emit("showAlert", {
            type: "info",
            msg:
              "Patient Scan loading for " +
              scan.PatientName +
              " complete. Ready to view patient scan.",
          });
          // If this update resulted in a failed scan and we're not still initializing, show a failure alert
        } else if (
          response.provisioningStatus === "Failed" &&
          scan.ProvisioningStatus === "InProgress" &&
          this.statusChecksTimer !== null
        ) {
          this.$root.$emit("showAlert", {
            type: "error",
            msg:
              "Patient Scan loading for " +
              scan.PatientName +
              " failed. Click to retry",
          });
        }
        // Finally, update the provisioning Status
        this.scans[
          this.scans.findIndex((el) => el.id === response.patientScanId)
        ]["ProvisioningStatus"] = response.provisioningStatus;
      });
      let promises = await this.scans.map(async (scan) => {
        // Only get a status update for Scans with InProgress status
        if (
          scan["ProvisioningStatus"] === "InProgress" ||
          updateAllScans ||
          scan["ProvisioningStatus"] === undefined
        ) {
          // Get the scan's metadata from the PSVPS
          const metadataResponse =
            await this.$patientScanViewerProvisioningApi.patientscansIdViewermetadataGet(
              scan.id
            );
          // Update the object's status with the one from backend response
          scan["ProvisioningStatus"] = metadataResponse.provisioningStatus;
        }
        return scan;
      });
      Promise.all(promises).then((scans) => {
        this.scans = scans;
      });
    },
    async startBackendStatusChecks() {
      // Once every updateScansWithLoadingStatusInSeconds seconds, we check the (provisioning) statuses of the scans
      this.statusChecksModalTimer = setInterval(() => {
        if (document.visibilityState === "visible") {
          // We count the total time the tab is visible.
          this.totalTimeVisible += this.$updateScansWithLoadingStatusInSeconds;
          // Every time the tab is visible for $updateAllScansInSeconds, we make a call to update all scans
          this.getStatusesForScans(
            this.totalTimeVisible % this.$updateAllScansInSeconds <=
              this.$updateScansWithLoadingStatusInSeconds
          );
        }
      }, this.$updateScansWithLoadingStatusInSeconds * 1000);
    },
    handleDicomButtonClick(item) {
      // Only try to open the dicom viewer if the scan succeeded
      if (item["ProvisioningStatus"] === "Completed") {
        this.openDicomViewer(item);
      } else if (item["ProvisioningStatus"] === "Unprovisioned") {
        this.scans = this.scans.map((scan) =>
          scan.id === item.id
            ? { ...scan, ProvisioningStatus: "InProgress" }
            : scan
        );
        this.provisionDicomViewer(item);
      } else if (item["ProvisioningStatus"] === "Failed") {
        item.ProvisioningStatus = "InProgress";
        this.provisionDicomViewer(item);
      } else {
        // eslint-disable-next-line
        console.error(
          "Unimplemented provisioning status: " +
            item["ProvisioningStatus"] +
            " button click"
        );
      }
    },
    provisionDicomViewer(item) {
      try {
        this.$patientScanViewerProvisioningApi.patientscansIdViewerGet(item.id);
      } catch (error) {
        let errorMsg = "";
        if (error.status === 409) {
          errorMsg = `Another scan with the same Study ID was detected, provisioning aborted`;
        } else {
          errorMsg = `Error ${error.status} while provisioning Patient Scan ${item.id}`;
        }
        this.showSpinner = false;
        this.$root.$emit("showAlert", {
          type: "error",
          msg: errorMsg,
        });
      }
    },
    async openDicomViewer(item) {
      // Show Loading status while we wait for backend
      item.ProvisioningStatus = "InProgress";
      try {
        // Try to get the URL of the viewer from the backend
        var response_obj =
          await this.$patientScanViewerProvisioningApi.patientscansIdViewerGet(
            item.id
          );
        var ohif_url = response_obj.ohifUrl;
        item.ProvisioningStatus = "Completed";

        // Open and focus on the viewer in a new tab
        var win = window.open(ohif_url, "_blank");
        win.focus();
      } catch (error) {
        let errorMsg = "";
        if (error.status === 409) {
          errorMsg = `Another scan with the same Study ID was detected, provisioning aborted`;
        } else {
          errorMsg = `Error ${error.status} while provisioning Patient Scan ${item.id}`;
        }
        this.showSpinner = false;
        this.$root.$emit("showAlert", {
          type: "error",
          msg: errorMsg,
        });
      }
    },
  },
  watch: {
    selectedStatus() {
      this.selectedUploadedBy = "All";
      this.getPatientScans();
    },
    selectedUploadedBy() {
      this.getPatientScans();
    },
    selectedUploadedWithin() {
      this.getPatientScans();
    },
  },
};
</script>

<style scoped>
footer .btn {
  margin: 1rem;
}
td .btn {
  font-size: 0.8rem;
}
.viewer-btn {
  width: 55%;
}
.modal-body {
  padding: 1rem 2rem;
}
.table {
  text-align: left;
}
.table-warning {
  background-color: aquamarine;
}
.download-btn {
  width: 40%;
  padding: 0.29rem 0.45rem;
  margin-left: 0.3rem;
}
#more-columns-dropdown {
  font-size: 0.6rem !important;
  float: right;
  margin: 0.1rem 0rem 0.1rem 3.5rem;
}
.column-dropdown {
  padding-left: 0.8rem;
  padding-top: 0.5rem;
}
#apply-columns-btn {
  margin: 0.8rem 2.3rem 0rem 1.8rem;
  font-size: 0.8rem;
}
</style>